
import Vue from 'vue';
import Contact from '../Contact.vue';
import DateTimePicker from '../DateTimePicker.vue';
import axios from 'axios';
import { EventBus } from '@/plugins/eventBus';

export default Vue.extend({
  name: 'WorkerCompetenceCheckModal',
  components: { Contact, DateTimePicker },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    competenceCheck: {
      type: Object,
      default: () => ({})
    },
    workerId: {
      type: [String, Number],
      required: true
    },
    isCreateMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      valid: true,
      saving: false,
      dateValidationError: '',
      resetKey: 0,
      showValidation: false, // Contrôle l'affichage des validations
      dateFromData: {
        dateObject: null as Date | null,
        dateString: '',
        timeString: ''
      },
      dateToData: {
        dateObject: null as Date | null,
        dateString: '',
        timeString: ''
      },
      dateCallData: {
        dateObject: null as Date | null,
        dateString: '',
        timeString: ''
      },
      formData: {
        company: '',
        company_id: null,
        contact_id: null as number | null,
        interlocutor: '' as string | null,
        qualification: '',
        date_from: '',
        date_to: '',
        date_call: '',
        is_take_back: false,
        exchange: '',
        contract_type: '',
        mobilePhoneNumber: '',
        is_prospect: null as boolean | null
      },
      contractTypes: [
        { text: this.$vuetify.lang.t('$vuetify.contract_type_list.temporary'), value: 'temporary' },
        { text: this.$vuetify.lang.t('$vuetify.contract_type_list.fixed_term_contract'), value: 'fixed term contract' },
        { text: this.$vuetify.lang.t('$vuetify.contract_type_list.permanent_contract'), value: 'permanent contract' },
        { text: this.$vuetify.lang.t('$vuetify.contract_type_list.cdii'), value: 'cdii' },
        { text: this.$vuetify.lang.t('$vuetify.contract_type_list.seasonal'), value: 'seasonal contract' },
        { text: this.$vuetify.lang.t('$vuetify.contract_type_list.internship'), value: 'internship' },
        { text: this.$vuetify.lang.t('$vuetify.contract_type_list.apprenticeship'), value: 'apprenticeship' }
      ],
      contactData: {
        id: null,
        first_name: '',
        last_name: '',
        email: null as string | null,
        phone: null as string | null,
        client_id: 0
      }
    };
  },
  computed: {
    dialog: {
      get(): boolean {
        return this.value;
      },
      set(value: boolean) {
        this.$emit('input', value);
      }
    },
    companyRules() {
      return (v: string) => !!v || this.$vuetify.lang.t('$vuetify.company_required');
    },
    qualificationRules() {
      return (v: string) => !!v || this.$vuetify.lang.t('$vuetify.qualification_required');
    },
    contractTypeRules() {
      return (v: string) => !!v || this.$vuetify.lang.t('$vuetify.contract_type_required');
    },
    isFormValid(): boolean {
      if (!this.showValidation) {
        return false;
      }
      const requiredFields = {
        company: (this as any).formData.company,
        qualification: (this as any).formData.qualification,
        contract_type: (this as any).formData.contract_type
      };
      const missingFields = Object.entries(requiredFields)
        .filter(([_, value]) => !value || value.trim() === '')
        .map(([field, _]) => field);
      return missingFields.length === 0 && (this as any).valid && !(this as any).dateValidationError;
    }
  },
  watch: {
    competenceCheck: {
      handler(newVal: any) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.loadFormData(newVal);
        } else {
          this.resetFormData();
        }
      },
      immediate: true,
      deep: true
    },
    contactData: {
      handler() {
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      },
      deep: true,
      immediate: true
    },
    dateFromData: {
      handler() {
        this.validateDates();
      },
      deep: true
    },
    dateToData: {
      handler() {
        this.validateDates();
      },
      deep: true
    },
    'formData.company': {
      handler(newVal: string) {
        if (newVal && newVal.trim() !== '') {
          this.showValidation = true;
        }
      }
    },
    'formData.qualification': {
      handler(newVal: string) {
        if (newVal && newVal.trim() !== '') {
          this.showValidation = true;
        }
      }
    },
    'formData.contract_type': {
      handler(newVal: string) {
        if (newVal && newVal.trim() !== '') {
          this.showValidation = true;
        }
      }
    },
    dialog: {
      handler(newVal: boolean) {
        if (newVal) {
          this.$nextTick(() => {
            const formRef = this.$refs.form as Vue & { resetValidation: () => void };
            if (formRef && typeof formRef.resetValidation === 'function') {
              formRef.resetValidation();
            }
          });
        }
      },
      immediate: false
    }
  },
  methods: {
    resetFormData() {
      const today = new Date();
      const todayFormatted = today.toISOString().split('T')[0];
      this.formData = {
        company: '',
        company_id: null,
        contact_id: null,
        interlocutor: null,
        qualification: '',
        date_from: '',
        date_to: '',
        date_call: todayFormatted,
        is_take_back: false,
        exchange: '',
        contract_type: '',
        mobilePhoneNumber: '',
        is_prospect: null
      };
      this.dateValidationError = '';
      this.valid = true;
      this.showValidation = false;
      this.dateFromData = { dateObject: null, dateString: '', timeString: '' };
      this.dateToData = { dateObject: null, dateString: '', timeString: '' };
      this.dateCallData = { dateObject: new Date(today), dateString: todayFormatted, timeString: '00:00' };
      this.resetContactData();
      this.resetKey++;
    },
    loadFormData(data: any) {
      this.formData = {
        company: this.getCompanyName(data.company, data.registrate_company_name),
        qualification: data.qualification || '',
        date_from: this.formatDate(data.date_from),
        date_to: this.formatDate(data.date_to),
        date_call: this.formatDate(data.date_call),
        is_take_back: data.is_take_back || false,
        exchange: data.exchange || '',
        contract_type: data.contract_type || '',
        mobilePhoneNumber: this.getMobilePhoneNumber(data.phone, data.registrate_company_phone),
        is_prospect: data.is_prospect !== undefined && data.is_prospect !== null ? Boolean(data.is_prospect) : null,
        company_id: data.company_id || null,
        contact_id: data.contact_id || null,
        interlocutor: data.interlocutor || null
      };
      this.initializeDateData(data);
      this.initializeContactData(data);
    },
    initializeDateData(data: any) {
      this.dateFromData = this.createDateData(data.date_from, false);
      this.dateToData = this.createDateData(data.date_to, false);
      this.dateCallData = this.createDateData(data.date_call, true);
    },
    createDateData(dateValue: any, includeTime: boolean) {
      const defaultData = {
        dateObject: null,
        dateString: '',
        timeString: includeTime ? '00:00' : ''
      };
      if (!dateValue) return defaultData;
      const date = new Date(dateValue);
      if (isNaN(date.getTime()) || !isFinite(date.getTime())) return defaultData;
      return {
        dateObject: date,
        dateString: this.formatDateForPicker(dateValue),
        timeString: includeTime ? this.formatTimeForPicker(dateValue) : '00:00'
      };
    },
    initializeContactData(data: any) {
      if (data.contact_id) {
        this.fetchContact();
      } else {
        this.resetContactData();
      }
    },
    resetContactData() {
      this.contactData = {
        id: null,
        first_name: '',
        last_name: '',
        email: null,
        phone: null,
        client_id: 0
      };
    },
    validateDates() {
      this.dateValidationError = '';
      if (!this.dateFromData.dateString || !this.dateToData.dateString) {
        return;
      }
      const fromDate = new Date(this.dateFromData.dateString + ' ' + (this.dateFromData.timeString || '00:00'));
      const toDate = new Date(this.dateToData.dateString + ' ' + (this.dateToData.timeString || '00:00'));
      if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
        return;
      }
      if (fromDate >= toDate) {
        this.dateValidationError = this.$vuetify.lang.t('$vuetify.date_from_must_be_before_date_to');
        this.valid = false;
      } else {
        this.valid = true;
      }
    },
    formatDate(date: any): string {
      if (!date) return '-';
      const d = new Date(date);
      if (isNaN(d.getTime()) || !isFinite(d.getTime())) return '-';
      return d.toLocaleDateString('fr-FR');
    },
    closeModal() {
      // Scroll modal content to top when closing
      const dialog = document.getElementsByClassName('v-dialog--active')[0] as HTMLElement;
      if (dialog) dialog.scrollTop = 0;
      // Close modal
      this.$emit('input', false);
    },
    async saveCompetenceCheck() {
      this.showValidation = true;
      // Force Vuetify to run validation and show errors
      const formRef = this.$refs.form as Vue & { validate: () => boolean };
      if (formRef && typeof formRef.validate === 'function') {
        formRef.validate();
      }
      this.validateDates();
      if (!this.isFormValid) {
        return;
      }
      this.saving = true;
      try {
        const data = this.prepareDataForSave();
        if (this.competenceCheck && this.competenceCheck.id) {
          await axios.put(`/v1/workers/${this.workerId}/competence_check/${this.competenceCheck.id}`, data);
          EventBus.$emit('snackbar', {
            message: this.$vuetify.lang.t('$vuetify.competence_check_updated_successfully'),
            color: 'success'
          });
          this.$emit('competence-check-updated');
        } else {
          await axios.post(`/v1/workers/${this.workerId}/competence_check`, data);
          EventBus.$emit('snackbar', {
            message: this.$vuetify.lang.t('$vuetify.competence_check_created_successfully'),
            color: 'success'
          });
          this.resetFormData();
          this.$emit('competence-check-created');
          this.closeModal();
        }
      } catch (error) {
        if ((error as any).response?.data === 'Invalid contract type.') {
          EventBus.$emit('snackbar', { color: 'error', message: this.$vuetify.lang.t('$vuetify.invalid_contract_type') });
        } else {
          EventBus.$emit('snackbar', { axiosError: error });
        }
      } finally {
        this.saving = false;
      }
    },
    prepareDataForSave() {
      const data: any = {
        company: this.formData.company,
        qualification: this.formData.qualification,
        contract_type: this.formData.contract_type,
        is_take_back: this.formData.is_take_back,
        exchange: this.formData.exchange,
        is_prospect: this.formData.is_prospect
      };
      if (this.dateFromData.dateString) {
        data.date_from = this.dateFromData.dateString + (this.dateFromData.timeString ? ' ' + this.dateFromData.timeString : '');
      }
      if (this.dateToData.dateString) {
        data.date_to = this.dateToData.dateString + (this.dateToData.timeString ? ' ' + this.dateToData.timeString : '');
      }
      if (this.dateCallData.dateString) {
        data.date_call = this.dateCallData.dateString + (this.dateCallData.timeString ? ' ' + this.dateCallData.timeString : '');
      }
      if (this.formData.interlocutor) {
        data.interlocutor = this.formData.interlocutor;
      }
      if (this.formData.mobilePhoneNumber) {
        data.phone = this.formData.mobilePhoneNumber;
      }
      return data;
    },
    async fetchContact() {
      try {
        const response = await axios.get(`/v1/contacts?company_id=${this.formData.company_id}`);
        const contact = response.data[0];
        this.contactData = {
          id: contact.id || null,
          first_name: contact.first_name || '',
          last_name: contact.last_name || '',
          email: contact.email || null,
          phone: contact.phone || null,
          client_id: contact.client_id || 0
        };
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      } catch {
        EventBus.$emit('snackbar', { color: 'error', message: this.$vuetify.lang.t('$vuetify.error_fetching_contact') });
      }
    },
    getCompanyName(company: any, registeredCompany: any): string {
      if (company && company !== '' && company !== null && company !== undefined) return company;
      if (registeredCompany && registeredCompany !== '' && registeredCompany !== null && registeredCompany !== undefined) return registeredCompany;
      return '';
    },
    updateContractType(value: string) {
      this.formData.contract_type = value;
    },
    formatDateForPicker(date: any): string {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime()) || !isFinite(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatTimeForPicker(dateTime: any): string {
      if (!dateTime) return '00:00';
      const d = new Date(dateTime);
      if (isNaN(d.getTime()) || !isFinite(d.getTime())) return '00:00';
      const hour = String(d.getHours()).padStart(2, '0');
      const minute = String(d.getMinutes()).padStart(2, '0');
      return `${hour}:${minute}`;
    },
    getMobilePhoneNumber(phone: any, registrateCompanyPhone: any): string {
      if (phone && phone !== '' && phone !== null && phone !== undefined) return phone;
      if (registrateCompanyPhone && registrateCompanyPhone !== '' && registrateCompanyPhone !== null && registrateCompanyPhone !== undefined) return registrateCompanyPhone;
      return '';
    },
    onContactSelected(contactId: number) {
      this.formData.contact_id = contactId;
      this.fetchContactById(contactId);
    },
    async fetchContactById(contactId: number) {
      try {
        const response = await axios.get(`/v1/contacts/${contactId}`);
        const contact = response.data;
        this.contactData = {
          id: contact.id || null,
          first_name: contact.first_name || '',
          last_name: contact.last_name || '',
          email: contact.email || null,
          phone: contact.phone || null,
          client_id: contact.client_id || 0
        };
        this.formData.interlocutor = `${contact.first_name} ${contact.last_name}`.trim();
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      } catch {
        EventBus.$emit('snackbar', { axiosError: this.$vuetify.lang.t('$vuetify.error_fetching_contact') });
      }
    }
  }
});
